/**
 * Страница списка смен швейного цеха.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  Button,
  DataCell,
  DataTable,
  Paper,
  ConfirmModal,
  MenuButton,
  PaginationControlled,
  CollapsibleListFilters,
  EmployeeFilterSelect,
  FilterFieldLabel,
  FilterNativeSelect,
  FiltersPanel,
  FilterTextInput,
  TableRow,
} from '../uikit';
import type { IDataTableColumn, ISortedTable, OrderDirectionType } from '../uikit';
import type { ShiftListItemDTO } from '../api/shifts';
import {
  findShifts,
  deleteShifts,
  findFilterShiftOrders,
  findFilterShiftStatuses,
  SHIFT_STATUS_LABELS,
  type FindShiftsParams,
} from '../api/shifts';
import { toUserMessage } from '../api/client';
import { findOrdersByIDs } from '../api/orders';
import { findEmployeesByIDs, type Employee } from '../api/employees';
import { IconEdit, IconDelete } from '../uikit/components/icons';
import { useAuth } from '../auth';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { formatFullName } from '../utils/formatFullName';
import { formatInteger, formatMoney } from '../utils/formatNumber';

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 -${({ theme }) => theme.space[2]}px 12px;
  padding-left: ${({ theme }) => theme.space[2]}px;
  padding-right: ${({ theme }) => theme.space[2]}px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrorRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  ${({ theme }) => theme.device.mobile} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const TableSection = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

interface RelatedMaps {
  ordersMap: Record<string, { title?: string }>;
  employeesMap: Record<string, { familyName?: string; givenName?: string; patronymic?: string }>;
}

/** Маршрут списка смен сотрудника — передаётся в state для возврата с детальной страницы. */
export type EmployeeShiftsListView = 'upcoming' | 'closed' | 'all';

export const EMPLOYEE_SHIFTS_LIST_PATH: Record<EmployeeShiftsListView, string> = {
  upcoming: '/shifts/upcoming',
  closed: '/shifts/closed',
  all: '/shifts/all',
};

/** Серверный фильтр по `closedAt` для списка смен сотрудника (вкладки маршрутов). */
function employeeListHasClosedAt(
  isEmployeeOnly: boolean,
  view: EmployeeShiftsListView | undefined,
): boolean | undefined {
  if (!isEmployeeOnly || view == null) return undefined;
  if (view === 'upcoming') return false;
  if (view === 'closed') return true;
  return undefined;
}

function getShiftColumns(
  canManage: boolean,
  onEdit: (item: ShiftListItemDTO) => void,
  onDelete: (item: ShiftListItemDTO) => void,
  related: RelatedMaps,
  options?: { employeeShiftsListPath?: string },
): IDataTableColumn<ShiftListItemDTO>[] {
  const cols: IDataTableColumn<ShiftListItemDTO>[] = [
    {
      key: 'orderId',
      header: 'Заказ',
      sortable: false,
      fixedWidth: 200,
      cellStyle: { px: 2 },
      render: (value, item) => {
        const id = value as string;
        const order = id ? related.ordersMap[id] : undefined;
        const display = order?.title ?? '—';
        const listPath = options?.employeeShiftsListPath;
        return (
          <Link
            to={`/shifts/${item.id}`}
            state={listPath ? { employeeShiftsList: listPath } : undefined}
            style={{ color: 'inherit', textDecoration: 'none', fontWeight: 500 }}
          >
            {display}
          </Link>
        );
      },
    },
    {
      key: 'employeeId',
      header: 'Сотрудник',
      sortable: false,
      fixedWidth: 200,
      cellStyle: { px: 2 },
      render: (value) => {
        const id = value as string;
        const emp = id ? related.employeesMap[id] : undefined;
        const display = emp
          ? formatFullName(emp.familyName, emp.givenName, emp.patronymic) || '—'
          : '—';
        return display;
      },
    },
    {
      key: 'shiftDate',
      header: 'Дата смены',
      sortable: true,
      fixedWidth: 120,
      cellStyle: { px: 2 },
      render: (value) => formatDate(value as string),
    },
    {
      key: 'status',
      header: 'Статус',
      sortable: true,
      fixedWidth: 130,
      cellStyle: { px: 2 },
      render: (value) => {
        const status = value as string;
        return status
          ? (SHIFT_STATUS_LABELS[status as keyof typeof SHIFT_STATUS_LABELS] ?? status)
          : '—';
      },
    },
    {
      key: 'closedAt',
      header: 'Закрыта',
      sortable: false,
      fixedWidth: 120,
      cellStyle: { px: 2 },
      render: (value) => formatDateTime(value as string | null),
    },
    {
      key: 'totalPlanQty',
      header: { text: 'План, шт', headerCellStyle: { textAlign: 'right' } },
      sortable: false,
      fixedWidth: 96,
      cellStyle: { px: 2, textAlign: 'right' },
      render: (_, item) => formatInteger(item.totalPlanQty ?? 0),
    },
    {
      key: 'totalFactQty',
      header: { text: 'Факт, шт', headerCellStyle: { textAlign: 'right' } },
      sortable: false,
      fixedWidth: 96,
      cellStyle: { px: 2, textAlign: 'right' },
      render: (_, item) => formatInteger(item.totalFactQty ?? 0),
    },
    {
      key: 'totalPayment',
      header: { text: 'Оплата, руб', headerCellStyle: { textAlign: 'right' } },
      sortable: false,
      fixedWidth: 120,
      cellStyle: { px: 2, textAlign: 'right' },
      render: (_, item) => formatMoney(item.totalPayment),
    },
  ];

  if (canManage) {
    cols.push({
      key: 'actions',
      header: 'Действия',
      sortable: false,
      fixedWidth: 84,
      truncate: false,
      cellStyle: { px: 1, textAlign: 'right' },
      render: (_, item) => (
        <div style={{ display: 'flex', gap: 0, justifyContent: 'flex-end' }}>
          <MenuButton
            size={28}
            aria-label="Редактировать"
            onClick={() => onEdit(item)}
          >
            <IconEdit />
          </MenuButton>
          {item.closedAt ? null : (
            <MenuButton
              size={28}
              aria-label="Удалить"
              onClick={() => onDelete(item)}
            >
              <IconDelete />
            </MenuButton>
          )}
        </div>
      ),
    });
  }

  return cols;
}

/** Итог к выплате: нижняя строка таблицы, визуально выделена. */
const ShiftsTotalFooterRow = styled(TableRow)`
  &:hover td {
    background: ${({ theme }) => theme.colors.highlight.blue25};
  }

  & > td {
    background: ${({ theme }) => theme.colors.highlight.blue25};
    border-top: 2px solid ${({ theme }) => theme.colors.primary.main};
    vertical-align: middle;
  }
`;

export interface ShiftsListPageProps {
  /** Для сотрудника: какой список показать (отдельный маршрут и пункт меню). */
  employeeListView?: EmployeeShiftsListView;
}

export function ShiftsListPage({ employeeListView }: ShiftsListPageProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  const isManagerOrAdmin =
    Boolean(user?.isCompanyAdmin) ||
    user?.miniappRole === 'manager' ||
    user?.miniappRole === 'administrator';

  const isEmployeeOnly = user?.miniappRole === 'employee' && !isManagerOrAdmin;

  const currentUserId = user?.userId ?? (user as { id?: string } | undefined)?.id;

  const [items, setItems] = useState<ShiftListItemDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPaymentSum, setTotalPaymentSum] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ordersMap, setOrdersMap] = useState<Record<string, { title?: string }>>({});
  const [employeesMap, setEmployeesMap] = useState<
    Record<string, { familyName?: string; givenName?: string; patronymic?: string }>
  >({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ShiftListItemDTO | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sort, setSort] = useState<ISortedTable>({ orderBy: 'shiftDate', orderDirection: 'DESC' });
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [orderFilterId, setOrderFilterId] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [shiftDateFrom, setShiftDateFrom] = useState('');
  const [shiftDateTo, setShiftDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [facetStatuses, setFacetStatuses] = useState<string[]>([]);
  const [facetOrderIds, setFacetOrderIds] = useState<string[]>([]);
  const [filterOptionsError, setFilterOptionsError] = useState<string | null>(null);

  const hasClosedAtForList = employeeListHasClosedAt(isEmployeeOnly, employeeListView);

  useLayoutEffect(() => {
    setPage(1);
  }, [employeeListView]);

  const loadFilterFacets = useCallback(async () => {
    setFilterOptionsError(null);
    try {
      const employeeIdParam = isManagerOrAdmin
        ? selectedEmployee?.id
        : currentUserId ?? undefined;

      const facetHasClosed =
        hasClosedAtForList !== undefined ? { hasClosedAt: hasClosedAtForList } : {};

      const [statusRes, orderRes] = await Promise.all([
        findFilterShiftStatuses({
          orderId: orderFilterId || undefined,
          employeeId: employeeIdParam,
          shiftDateFrom: shiftDateFrom || undefined,
          shiftDateTo: shiftDateTo || undefined,
          ...facetHasClosed,
        }),
        findFilterShiftOrders({
          employeeId: employeeIdParam,
          shiftDateFrom: shiftDateFrom || undefined,
          shiftDateTo: shiftDateTo || undefined,
          status: statusFilter || undefined,
          ...facetHasClosed,
        }),
      ]);

      const statuses = statusRes.statuses ?? [];
      const orderIds = orderRes.orderIds ?? [];
      setFacetStatuses(statuses);
      setFacetOrderIds(orderIds);

      const orderIdsForTitles = [...orderIds];
      if (orderFilterId && !orderIdsForTitles.includes(orderFilterId)) {
        orderIdsForTitles.push(orderFilterId);
      }
      if (orderIdsForTitles.length > 0) {
        const ordersRes = await findOrdersByIDs(orderIdsForTitles);
        setOrdersMap((prev) => ({ ...prev, ...ordersRes.items }));
      }
    } catch (err) {
      setFilterOptionsError(toUserMessage(err));
    }
  }, [
    currentUserId,
    isManagerOrAdmin,
    orderFilterId,
    selectedEmployee?.id,
    shiftDateFrom,
    shiftDateTo,
    statusFilter,
    hasClosedAtForList,
  ]);

  useEffect(() => {
    void loadFilterFacets();
  }, [loadFilterFacets]);

  const loadShifts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const employeeIdParam = isManagerOrAdmin
        ? selectedEmployee?.id
        : currentUserId ?? undefined;

      const baseParams: FindShiftsParams = {
        orderId: orderFilterId || undefined,
        employeeId: employeeIdParam,
        shiftDateFrom: shiftDateFrom || undefined,
        shiftDateTo: shiftDateTo || undefined,
        status: statusFilter || undefined,
        order: [{ field: sort.orderBy ?? 'shiftDate', direction: sort.orderDirection ?? 'DESC' }],
        page,
        itemsPerPage,
      };
      if (hasClosedAtForList !== undefined) {
        baseParams.hasClosedAt = hasClosedAtForList;
      }

      const result = await findShifts(baseParams);
      const shiftItems = result.items ?? [];
      const resultCount = result.count ?? 0;

      setItems(shiftItems);
      setTotalPaymentSum(result.totalPaymentSum ?? null);

      const orderIds = [...new Set(shiftItems.map((s) => s.orderId).filter(Boolean))];
      if (orderFilterId && !orderIds.includes(orderFilterId)) {
        orderIds.push(orderFilterId);
      }
      const employeeIds = [...new Set(shiftItems.map((s) => s.employeeId).filter(Boolean))];

      const [ordersRes, employeesRes] = await Promise.allSettled([
        orderIds.length > 0 ? findOrdersByIDs(orderIds) : Promise.resolve({ items: {} }),
        employeeIds.length > 0 ? findEmployeesByIDs(employeeIds) : Promise.resolve({ items: {} }),
      ]);

      setOrdersMap((prev) =>
        ordersRes.status === 'fulfilled' ? { ...prev, ...ordersRes.value.items } : prev,
      );
      setEmployeesMap(employeesRes.status === 'fulfilled' ? employeesRes.value.items : {});
      setTotal(resultCount);
    } catch (err) {
      setItems([]);
      setEmployeesMap({});
      setTotal(0);
      setTotalPaymentSum(null);
      setLoadError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }, [
    currentUserId,
    hasClosedAtForList,
    isManagerOrAdmin,
    itemsPerPage,
    page,
    orderFilterId,
    selectedEmployee?.id,
    shiftDateFrom,
    shiftDateTo,
    statusFilter,
    sort.orderBy,
    sort.orderDirection,
  ]);

  useEffect(() => {
    void loadShifts();
  }, [loadShifts]);

  const handleSort = (key: string, direction: OrderDirectionType) => {
    setSort({ orderBy: key, orderDirection: direction });
    setPage(1);
  };

  const handleEditClick = (shift: ShiftListItemDTO) => {
    navigate(`/shifts/${shift.id}/edit`);
  };

  const handleDeleteClick = (shift: ShiftListItemDTO) => {
    setDeleteTarget(shift);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setLoadError(null);
    try {
      await deleteShifts([deleteTarget.id]);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      await loadShifts();
    } catch (err) {
      setLoadError(toUserMessage(err));
    } finally {
      setDeleteLoading(false);
    }
  };

  const orderOptionsIds = useMemo(() => {
    const s = new Set(facetOrderIds);
    if (orderFilterId) s.add(orderFilterId);
    return [...s].sort((a, b) => {
      const ta = ordersMap[a]?.title ?? a;
      const tb = ordersMap[b]?.title ?? b;
      return String(ta).localeCompare(String(tb), 'ru');
    });
  }, [facetOrderIds, orderFilterId, ordersMap]);

  const statusOptions = useMemo(() => {
    const s = new Set(facetStatuses);
    if (statusFilter) s.add(statusFilter);
    return [...s].sort();
  }, [facetStatuses, statusFilter]);

  const employeeShiftsListPath = employeeListView
    ? EMPLOYEE_SHIFTS_LIST_PATH[employeeListView]
    : undefined;

  const shiftColumns = getShiftColumns(
    isManagerOrAdmin,
    handleEditClick,
    handleDeleteClick,
    { ordersMap, employeesMap },
    employeeShiftsListPath ? { employeeShiftsListPath } : undefined,
  );

  const pageTitle = (() => {
    if (!isEmployeeOnly) return 'Смены';
    switch (employeeListView) {
      case 'upcoming':
        return 'Предстоящие смены';
      case 'closed':
        return 'Закрытые смены';
      case 'all':
        return 'Все смены';
      default:
        return 'Смены';
    }
  })();

  const tablePlaceholder = (() => {
    if (!isEmployeeOnly || !employeeListView) return { text: 'Смены не найдены' as const };
    switch (employeeListView) {
      case 'upcoming':
        return { text: 'Нет предстоящих смен' as const };
      case 'closed':
        return { text: 'Нет закрытых смен' as const };
      case 'all':
        return { text: 'Смены не найдены' as const };
      default:
        return { text: 'Смены не найдены' as const };
    }
  })();

  const shiftsActiveFilterCount = useMemo(() => {
    let n = 0;
    if (orderFilterId) n += 1;
    if (isManagerOrAdmin && selectedEmployee) n += 1;
    if (shiftDateFrom) n += 1;
    if (shiftDateTo) n += 1;
    if (statusFilter) n += 1;
    return n;
  }, [
    isManagerOrAdmin,
    orderFilterId,
    selectedEmployee,
    shiftDateFrom,
    shiftDateTo,
    statusFilter,
  ]);

  const totalPaymentSummary: { label: string; value: string } | null =
    loadError == null && totalPaymentSum != null && String(totalPaymentSum).trim() !== ''
      ? { label: 'Итого к выплате', value: `${formatMoney(totalPaymentSum)} руб` }
      : null;

  const paymentColumnIndex = shiftColumns.findIndex((c) => c.key === 'totalPayment');
  const footerLeadingColSpan = paymentColumnIndex >= 0 ? paymentColumnIndex : shiftColumns.length;
  const footerColumnsAfterPayment =
    paymentColumnIndex >= 0 ? shiftColumns.slice(paymentColumnIndex + 1) : [];
  const paymentColumnDef =
    paymentColumnIndex >= 0 ? shiftColumns[paymentColumnIndex] : undefined;

  const shiftsTableFooter =
    totalPaymentSummary != null ? (
      <ShiftsTotalFooterRow $horizontalLine $backlightLine={false}>
        <DataCell
          aria-live="polite"
          colSpan={footerLeadingColSpan}
          $backlightLine={false}
          $truncate={false}
          $defaultHeight={48}
          px={2}
          style={{
            fontSize: theme.fontSizes[5],
            fontWeight: 600,
            color: theme.colors.text.secondary,
          }}
        >
          {totalPaymentSummary.label}:
        </DataCell>
        <DataCell
          $backlightLine={false}
          $truncate={false}
          $defaultHeight={48}
          $fixedWidth={paymentColumnDef?.fixedWidth}
          px={2}
          textAlign="right"
          style={{
            fontSize: theme.fontSizes[5],
            fontWeight: 600,
            color: theme.colors.text.primary,
          }}
        >
          {totalPaymentSummary.value}
        </DataCell>
        {footerColumnsAfterPayment.map((col) => (
          <DataCell
            key={`shift-total-foot-${col.key}`}
            $backlightLine={false}
            $truncate={false}
            $defaultHeight={48}
            $fixedWidth={col.fixedWidth}
            px={col.key === 'actions' ? 1 : 2}
            textAlign="right"
          />
        ))}
      </ShiftsTotalFooterRow>
    ) : undefined;

  const renderShiftTables = () => (
    <TableSection>
      <DataTable
        data={items}
        columns={shiftColumns}
        orderBy={sort.orderBy}
        orderDirection={sort.orderDirection}
        onSort={handleSort}
        loading={loading}
        settings={{ horizontalLine: true, backlightLine: true }}
        minHeight={0}
        height="100%"
        placeholder={tablePlaceholder}
        footer={shiftsTableFooter}
      />
    </TableSection>
  );

  if (isManagerOrAdmin && employeeListView != null) {
    return <Navigate to="/shifts" replace />;
  }
  if (isEmployeeOnly && employeeListView == null) {
    return <Navigate to={EMPLOYEE_SHIFTS_LIST_PATH.upcoming} replace />;
  }

  return (
    <>
      <Paper padding={16} shadow={0} style={{ marginBottom: 0, height: '100%' }}>
        <PageLayout>
          <HeaderRow>
            <PageTitle>{pageTitle}</PageTitle>
            {isManagerOrAdmin && (
              <Button
                variant="filled"
                color="primary"
                size="s"
                onClick={() => navigate('/shifts/new')}
              >
                Создать смену
              </Button>
            )}
          </HeaderRow>
          <CollapsibleListFilters activeFilterCount={shiftsActiveFilterCount}>
            <FiltersPanel $borderless>
          <FilterFieldLabel htmlFor="shift-order-filter">
            Заказ:
            <FilterNativeSelect
              id="shift-order-filter"
              value={orderFilterId}
              onChange={(e) => {
                setOrderFilterId(e.target.value);
                setPage(1);
              }}
              aria-label="Фильтр по заказу"
              $minWidthPx={160}
              $maxWidthPx={220}
            >
              <option value="">Все</option>
              {orderOptionsIds.map((oid) => (
                <option key={oid} value={oid}>
                  {ordersMap[oid]?.title ?? '—'}
                </option>
              ))}
            </FilterNativeSelect>
          </FilterFieldLabel>
          {isManagerOrAdmin && (
            <FilterFieldLabel htmlFor="shift-employee-filter">
              Сотрудник:
              <EmployeeFilterSelect
                id="shift-employee-filter"
                value={selectedEmployee}
                onChange={(employee) => {
                  setSelectedEmployee(employee);
                  setPage(1);
                }}
                placeholder="Сотрудник"
                aria-label="Фильтр по сотруднику"
              />
            </FilterFieldLabel>
          )}
          <FilterFieldLabel htmlFor="shift-date-from">
            С:
            <FilterTextInput
              id="shift-date-from"
              type="date"
              value={shiftDateFrom}
              onChange={(e) => {
                setShiftDateFrom(e.target.value);
                setPage(1);
              }}
              $maxWidthPx={140}
              aria-label="Дата с"
            />
          </FilterFieldLabel>
          <FilterFieldLabel htmlFor="shift-date-to">
            По:
            <FilterTextInput
              id="shift-date-to"
              type="date"
              value={shiftDateTo}
              onChange={(e) => {
                setShiftDateTo(e.target.value);
                setPage(1);
              }}
              $maxWidthPx={140}
              aria-label="Дата по"
            />
          </FilterFieldLabel>
          <FilterFieldLabel htmlFor="shift-status-filter">
            Статус:
            <FilterNativeSelect
              id="shift-status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              aria-label="Фильтр по статусу"
            >
              <option value="">Все</option>
              {statusOptions.map((st) => (
                <option key={st} value={st}>
                  {SHIFT_STATUS_LABELS[st as keyof typeof SHIFT_STATUS_LABELS] ?? st}
                </option>
              ))}
            </FilterNativeSelect>
          </FilterFieldLabel>
            </FiltersPanel>
          </CollapsibleListFilters>
          {filterOptionsError && (
            <ErrorRow role="alert">
              <span style={{ color: theme.colors.error.main, fontSize: theme.fontSizes[5] }}>{filterOptionsError}</span>
              <Button
                variant="outlined"
                color="primary"
                size="s"
                onClick={() => void loadFilterFacets()}
              >
                Повторить
              </Button>
            </ErrorRow>
          )}
          {renderShiftTables()}
          {loadError && (
            <ErrorRow role="alert">
              <span style={{ color: theme.colors.error.main, fontSize: theme.fontSizes[5] }}>{loadError}</span>
              <Button variant="outlined" color="primary" size="s" onClick={() => void loadShifts()}>
                Повторить
              </Button>
            </ErrorRow>
          )}
          <PaginationControlled
            page={page}
            itemsPerPage={itemsPerPage}
            totalItems={total}
            setPage={setPage}
            setItemsPerPage={setItemsPerPage}
          />
        </PageLayout>
      </Paper>
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Удалить смену?"
        message={
          deleteTarget ? (
            <>Вы уверены, что хотите удалить смену от {formatDate(deleteTarget.shiftDate)}?</>
          ) : undefined
        }
        confirmText="Удалить"
        cancelText="Отмена"
        confirmColor="error"
        loading={deleteLoading}
      />
    </>
  );
}
